import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/useReveal";
import { getDemoStats } from "@/demo/demoApi";

export default function Hero({ onOpenDemo }) {
    const [demoCount, setDemoCount] = useState(null);
    useEffect(() => { getDemoStats().then((d) => setDemoCount(d.count)).catch(() => {}); }, []);
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-28 sm:pt-32 lg:pt-36 pb-24 lg:pb-32"
        >
            {/* soft purple ambient glow */}
            <div className="pointer-events-none absolute left-1/2 top-40 -translate-x-1/2 h-[520px] w-[820px] max-w-[95vw] rounded-full opacity-70 anim-pulse-glow"
                 style={{ background: "radial-gradient(closest-side, rgba(139,92,246,0.28), rgba(139,92,246,0.05) 60%, transparent 75%)" }}
            />

            <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                {/* Hero Logo Coin — merged with white background */}
                <div className="flex justify-center">
                    <div
                        data-testid="hero-logo"
                        className="anim-fade-up relative"
                    >
                        {/* soft purple halo behind coin */}
                        <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0 -m-8 rounded-full anim-pulse-glow"
                            style={{
                                background:
                                    "radial-gradient(closest-side, rgba(139,92,246,0.35), rgba(139,92,246,0.06) 55%, transparent 72%)",
                            }}
                        />
                        <img
                            src="/logo.png"
                            alt="CEO's GPT"
                            className="logo-blend relative h-40 w-40 sm:h-48 sm:w-48 lg:h-[230px] lg:w-[230px] object-contain anim-float-slow"
                            style={{
                                filter:
                                    "drop-shadow(0 24px 40px rgba(139,92,246,0.28)) drop-shadow(0 8px 16px rgba(17,17,17,0.12))",
                            }}
                        />
                    </div>
                </div>

                {/* Eyebrow */}
                <div className="mt-6 flex justify-center">
                    <div
                        data-testid="hero-eyebrow"
                        className="anim-fade-up inline-flex items-center gap-2 rounded-full border border-black/5 bg-white/70 backdrop-blur-md px-3.5 py-1.5 text-[11px] font-medium tracking-[0.18em] uppercase text-neutral-700"
                        style={{ animationDelay: "0.03s" }}
                    >
                        <span className="relative inline-flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-violet-500 opacity-70 animate-ping"></span>
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-violet-500"></span>
                        </span>
                        The Decision Operating System
                    </div>
                </div>

                {/* Headline */}
                <h1
                    data-testid="hero-heading"
                    className="anim-fade-up mt-6 font-display text-center text-[42px] sm:text-6xl lg:text-[80px] leading-[1.02] tracking-tight font-light text-black"
                    style={{ animationDelay: "0.05s" }}
                >
                    Decide Like{" "}
                    <span className="relative inline-block">
                        <span className="italic font-normal text-neutral-800">a Boss.</span>
                        <span
                            aria-hidden
                            className="absolute -bottom-1 left-0 right-0 h-[6px] rounded-full opacity-70"
                            style={{
                                background:
                                    "linear-gradient(90deg, rgba(139,92,246,0), rgba(139,92,246,0.55), rgba(139,92,246,0))",
                            }}
                        />
                    </span>
                </h1>

                <p
                    data-testid="hero-subheading"
                    className="anim-fade-up mt-6 max-w-2xl mx-auto text-center text-[15px] sm:text-lg leading-relaxed text-neutral-600"
                    style={{ animationDelay: "0.15s" }}
                >
                    A CEO’s command centre to{" "}
                    <span className="font-medium text-neutral-800">Converse</span> ·{" "}
                    <span className="font-medium text-neutral-800">Know</span> ·{" "}
                    <span className="font-medium text-neutral-800">Decide</span>.
                </p>

                {/* CTAs */}
                <div
                    className="anim-fade-up mt-9 flex flex-col sm:flex-row items-center justify-center gap-3"
                    style={{ animationDelay: "0.25s" }}
                >
                    <Link
                        to="/demo"
                        data-testid="hero-primary-cta"
                        className="group inline-flex items-center gap-2 bg-black text-white text-sm font-medium pl-5 pr-2 py-2 rounded-full hover:shadow-[0_20px_50px_-15px_rgba(139,92,246,0.6)] transition-all"
                    >
                        <span className="py-1.5 inline-flex items-center gap-2">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
                            Demo as CEO of EVOLVE
                        </span>
                        {demoCount != null && (
                            <span data-testid="hero-demo-count" className="hidden sm:inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white/90">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                {demoCount.toLocaleString()} took the demo
                            </span>
                        )}
                        <span className="ml-1 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white text-black transition-transform group-hover:translate-x-0.5">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </Link>
                </div>

                {/* Hero visual */}
                <div id="ceo-aichat" className="relative mt-16 sm:mt-20 scroll-mt-28">
                    <div className="text-center mb-8 sm:mb-10">
                        <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-violet-600" data-testid="ceo-aichat-eyebrow">CEO’s AI Chat</div>
                        <h2 className="mt-2 font-display text-2xl sm:text-3xl font-light tracking-tight text-black">
                            Your CEO Command Center, <span className="italic font-normal text-neutral-700">live</span>.
                        </h2>
                        <p className="mt-2 text-[13.5px] sm:text-base text-neutral-500 max-w-xl mx-auto">
                            Ask anything about your business — get insights, money impact, and executable Decision Cards in one conversation.
                        </p>
                    </div>
                    <HeroMockup />
                </div>

                {/* Trust row */}
                <div className="mt-16 sm:mt-24 flex flex-wrap justify-center items-center gap-x-10 gap-y-3 text-[11px] font-medium tracking-[0.24em] uppercase text-neutral-400">
                    <span>Founders</span>
                    <span className="text-neutral-300">·</span>
                    <span>Operators</span>
                    <span className="text-neutral-300">·</span>
                    <span>10 AI Agents</span>
                    <span className="text-neutral-300">·</span>
                    <span>Business Scan AI</span>
                    <span className="text-neutral-300">·</span>
                    <span>Money Meter</span>
                </div>
            </div>
        </section>
    );
}

const CHAT_TURNS = [
    {
        q: "Can I spend ₹5 lakh on marketing this month?",
        a: "Finance is safe, Sales pipeline supports it, Marketing ROI is strong.",
        chips: [
            ["Cash", "₹18.4L"],
            ["Pipeline", "+18%"],
            ["ROI", "3.1x"],
        ],
        card: {
            title: "Approve ₹5L Marketing Budget",
            tone: "positive",
            stats: [
                { k: "Expected Revenue", v: "+₹14L", t: "text-emerald-600" },
                { k: "Expected Profit", v: "+₹3.2L", t: "text-emerald-600" },
                { k: "Money Meter", v: "72 → 81", t: "text-violet-600" },
            ],
            interactive: true,
        },
    },
    {
        q: "Should we hire 3 senior engineers this quarter?",
        a: "Runway drops by ~2 months. Delay by 45 days for a safer cash position.",
        chips: [
            ["Runway", "4.5 → 2.5 mo"],
            ["Burn", "+₹6.2L/mo"],
            ["Impact", "High"],
        ],
        card: {
            title: "Delay Senior Hires by 45 days",
            tone: "warning",
            stats: [
                { k: "Cash Saved", v: "+₹18.6L", t: "text-emerald-600" },
                { k: "Runway", v: "+2 months", t: "text-emerald-600" },
                { k: "Money Meter", v: "72 → 76", t: "text-violet-600" },
            ],
        },
    },
    {
        q: "Approve ₹40L purchase order to vendor Acme?",
        a: "Cash flow supports it. Extend vendor payment cycle by 15 days for cushion.",
        chips: [
            ["Cash post", "₹22L"],
            ["Vendor NPS", "8.4"],
            ["Margin", "+₹12L"],
        ],
        card: {
            title: "Approve PO · Extend Terms 15d",
            tone: "positive",
            stats: [
                { k: "Expected Margin", v: "+₹12L", t: "text-emerald-600" },
                { k: "Cash Buffer", v: "45 days", t: "text-emerald-600" },
                { k: "Money Meter", v: "72 → 78", t: "text-violet-600" },
            ],
        },
    },
    {
        q: "Cut marketing spend by 30% to preserve cash?",
        a: "Not recommended. Pipeline drops ~22% next quarter and CAC rises.",
        chips: [
            ["Pipeline", "-22%"],
            ["CAC", "+18%"],
            ["Revenue Q+1", "-₹9L"],
        ],
        card: {
            title: "Reject Marketing Cut",
            tone: "negative",
            stats: [
                { k: "Revenue Loss", v: "-₹9L", t: "text-rose-600" },
                { k: "Pipeline Risk", v: "High", t: "text-rose-600" },
                { k: "Money Meter", v: "72 → 68", t: "text-violet-600" },
            ],
        },
    },
];

function HeroMockup() {
    const [status, setStatus] = useState("idle"); // idle | approved | rejected

    return (
        <div className="relative mx-auto max-w-4xl">
            {/* soft platform */}
            <div className="pointer-events-none absolute -inset-4 sm:-inset-10 rounded-[40px] blur-2xl opacity-70"
                 style={{
                     background:
                         "radial-gradient(60% 60% at 50% 40%, rgba(139,92,246,0.15), transparent 70%)",
                 }}
            />

            <div
                data-testid="hero-chat-mockup"
                className="relative glass-strong rounded-[32px] p-4 sm:p-6 overflow-hidden"
            >
                {/* chrome */}
                <div className="flex items-center justify-between px-2">
                    <div className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-neutral-200" />
                        <span className="h-3 w-3 rounded-full bg-neutral-200" />
                        <span className="h-3 w-3 rounded-full bg-neutral-200" />
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-neutral-400 font-medium tracking-wider">
                        <img src={LOGO_URL} alt="" className="logo-blend h-4 w-4 object-contain opacity-90" />
                        CEO COMMAND CENTER
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-neutral-400 font-medium">
                        <span className="relative inline-flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping"></span>
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                        </span>
                        live
                    </div>
                </div>

                {/* Scrolling chat feed */}
                <div className="relative mt-5 h-[420px] sm:h-[520px] overflow-hidden chat-mask">
                    <div className="chat-scroll flex flex-col gap-6">
                        {[...CHAT_TURNS, ...CHAT_TURNS].map((turn, i) => (
                            <ChatTurn
                                key={i}
                                turn={turn}
                                status={status}
                                setStatus={setStatus}
                                isFirst={i === 0}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChatTurn({ turn, status, setStatus, isFirst }) {
    return (
        <div className="grid gap-3">
            {/* CEO message */}
            <div className="flex justify-end">
                <div className="max-w-[85%] bg-black text-white rounded-3xl rounded-tr-md px-5 py-3.5 text-[14px] leading-relaxed shadow-[0_10px_30px_-15px_rgba(0,0,0,0.5)]">
                    {turn.q}
                </div>
            </div>

            {/* AI response */}
            <div className="flex items-start gap-3">
                <div className="mt-0.5 h-8 w-8 shrink-0 rounded-full overflow-hidden ring-1 ring-black/5 bg-white flex items-center justify-center">
                    <img src={LOGO_URL} alt="" className="logo-blend h-8 w-8 object-contain" />
                </div>
                <div className="max-w-[85%] bg-white/80 border border-black/5 rounded-3xl rounded-tl-md px-5 py-3.5 text-[14px] text-neutral-800 leading-relaxed">
                    {turn.a}
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {turn.chips.map(([k, v]) => (
                            <span
                                key={k}
                                className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] text-neutral-600"
                            >
                                <span className="text-neutral-400">{k}</span>
                                <span className="font-semibold text-neutral-800">{v}</span>
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* decision card */}
            <div className="mt-1">
                <DecisionCard
                    card={turn.card}
                    status={status}
                    setStatus={setStatus}
                    interactive={isFirst && turn.card.interactive}
                />
            </div>
        </div>
    );
}

function DecisionCard({ card, status, setStatus, interactive }) {
    const tone = card.tone || "positive";
    const glowColor =
        tone === "negative"
            ? "rgba(244,63,94,0.32)"
            : tone === "warning"
                ? "rgba(245,158,11,0.32)"
                : "rgba(139,92,246,0.35)";

    // If this is the first / interactive card use dynamic status, else derive from tone.
    const badge = interactive
        ? status === "approved"
            ? { label: "APPROVED", cls: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" }
            : status === "rejected"
                ? { label: "REJECTED", cls: "bg-rose-50 text-rose-700", dot: "bg-rose-500" }
                : { label: "PENDING", cls: "bg-violet-50 text-violet-700", dot: "bg-violet-500" }
        : tone === "negative"
            ? { label: "REJECT", cls: "bg-rose-50 text-rose-700", dot: "bg-rose-500" }
            : tone === "warning"
                ? { label: "REVIEW", cls: "bg-amber-50 text-amber-700", dot: "bg-amber-500" }
                : { label: "PENDING", cls: "bg-violet-50 text-violet-700", dot: "bg-violet-500" };

    const testId = interactive ? "hero-decision-card" : undefined;

    return (
        <div
            data-testid={testId}
            className="relative rounded-3xl border border-black/5 bg-white p-5 sm:p-6 overflow-hidden glow-purple-sm"
        >
            {/* corner glow */}
            <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full opacity-80"
                 style={{ background: `radial-gradient(closest-side, ${glowColor}, transparent 70%)` }}
            />

            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-violet-600">
                        Decision Card
                    </div>
                    <div className="mt-1 font-display text-[22px] sm:text-2xl font-medium tracking-tight text-black">
                        {card.title}
                    </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${badge.cls}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${badge.dot}`}></span>
                    {badge.label}
                </span>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
                {card.stats.map((s) => (
                    <div key={s.k} className="rounded-2xl bg-neutral-50/70 border border-black/5 p-3">
                        <div className="text-[10px] font-medium tracking-[0.18em] uppercase text-neutral-500">
                            {s.k}
                        </div>
                        <div className={`mt-1.5 font-display text-xl sm:text-2xl font-medium ${s.t}`}>
                            {s.v}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
                <button
                    onClick={interactive ? () => setStatus("approved") : undefined}
                    data-testid={interactive ? "decision-approve" : undefined}
                    className="inline-flex items-center gap-1.5 bg-black text-white text-[13px] font-medium px-4 py-2 rounded-full hover:bg-neutral-800 transition-colors"
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Approve
                </button>
                <button
                    data-testid={interactive ? "decision-simulate" : undefined}
                    className="inline-flex items-center gap-1.5 bg-white text-black border border-black/10 text-[13px] font-medium px-4 py-2 rounded-full hover:bg-neutral-50 transition-colors"
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M3 12a9 9 0 1015-6.7M21 3v6h-6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Simulate
                </button>
                <button
                    onClick={interactive ? () => setStatus("rejected") : undefined}
                    data-testid={interactive ? "decision-reject" : undefined}
                    className="inline-flex items-center gap-1.5 bg-white text-neutral-600 border border-black/10 text-[13px] font-medium px-4 py-2 rounded-full hover:bg-neutral-50 transition-colors"
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                    </svg>
                    Reject
                </button>
            </div>
        </div>
    );
}
